<template>
    <div>
        <!-- Header -->
        <div v-if="!disableHeader"  class="tw-border tw-bg-gray-50 tw-p-2 sm:tw-flex tw-items-center tw-justify-between">
            <div class="tw-flex-grow">
                <slot name="header" :search="search" :add-parameter="addParameter" :query-parameters="additionalParams" :refresh="refresh" :currentSearch="currentSearch"></slot>
                
            </div>

            <div class="tw-inline tw-relative">
                <div class="input-group">
                    <span class="input-group-btn">
                        <button type="button" class="btn btn-primary" @click="applySearch"><i class="fa fa-search"></i></button>
                    </span>
                    <input type="text" v-model="search" class="form-control" autocomplete="off" spellcheck="false" :placeholder="placeholder" @keydown.enter="applySearch">
                </div>

                <div v-show="search != ''" class="tw-absolute tw-inset-y-0 tw-right-0 tw-mr-2 tw-z-10">
                    <div class="tw-flex tw-items-center tw-h-full">
                        <i class="fa fa-times-circle tw-text-xl" @click="clearSearch"></i>
                    </div>
                </div>
            </div>
        </div>

        <div class="tw-relative tw-overflow-x-auto">
            <!-- Main Table Content -->
            <table class="table table-vcenter tw-border tw-min-w-full tw-m-0" :class="[tableClass, {'table-striped':striped}]">
                <thead v-if="!disableColumns">
                    <slot name="columns" :sort="sort" :rows="rows"></slot>
                </thead>
                <tbody>
                    <template v-if="!error && hasItems">
                        <slot name="rows" :rows="rows" :store="store" :refresh-table="refresh" :remove="removeRow"></slot>
                    </template>
                </tbody>
            </table>

            <!-- Loading Element -->
            <div v-show="loading" class="tw-h-full tw-inset-x-0 tw-top-0" :class="{ 'tw-absolute':hasItems }">
                <div class="tw-flex tw-h-full tw-items-center tw-bg-gray-100 tw-bg-opacity-50 tw-border tw-border-t-0 tw-p-2">
                    <div class="progress progress-striped active tw-mb-0 tw-w-full">
                        <div class="progress-bar progress-bar-info" style="width: 100%">Loading</div>
                    </div>
                </div>
            </div>

            <!-- No Results Element -->
            <div v-show="!loading && !error && !hasItems">
                <slot name="empty">
                    <div class="tw-border tw-border-t-0 tw-border-b-0 tw-p-2" :class="{'tw-bg-gray-50':striped}">
                        {{emptyMessage}}
                    </div>
                </slot>
            </div>
                
            <!-- Error Notification -->
            <div v-show="error" class="tw-border tw-border-t-0 tw-border-b-0 tw-p-2 tw-bg-gray-50">Error Retrieving Data</div>
        </div>

        <!-- Footer and Pagination -->
        <div v-if="!disableFooter" class="tw-border tw-p-4 sm:tw-flex sm:tw-items-center">
            <div class="tw-flex tw-flex-grow tw-items-center tw-justify-end sm:tw-justify-between">
                <div class="tw-hidden sm:tw-block"><strong>{{ results }}</strong> Results <span v-show="currentSearch != ''">for "{{ currentSearch }}"</span></div>

                <div v-if="paginate">
                    <slot name="pagination">
                        <table-pagination v-if="links" :links="links" @paged="getPage"></table-pagination>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Pagination from './BasePagination.vue';

export default {
    name: "DataTable",

    components: {
        'table-pagination': Pagination
    },

    props: {
        source: {
            type: String,
            required: true
        },
        uriBindings: {
            type: Object,
            default: function() {
                return {};
            }
        },
        queryParameters: {
            type: Object,
            default: function() {
                return {};
            }
        },
        paginate: {
            type: Boolean,
            default: true
        },
        poll: {
            type: Number,
            default: null
        },
        striped: {
            type: Boolean,
            default: true
        },
        tableClass: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: 'Search'
        },
        disableColumns: {
            type: Boolean,
            default: false
        },
        disableHeader: {
            type: Boolean,
            default: false
        },
        disableFooter: {
            type: Boolean,
            default: false
        },
        emptyMessage: {
            type: String,
            default: 'No Results Found'
        },
        updateOn: {
            type: Array,
            default: function() {
                return [];
            }
        }
    },

    data() {
        return {
            columns: [],
            sort: {
                column: 'id',
                direction: 'desc',
            },
            additionalParams: this.queryParameters,
            store: {},
            currentSearch: '',
            currentPage: 1,
            search: '',
            results: 0,
            links: null,
            rows: [],
            loading: false,
            error: false,
            interval: null,
        }
    },

    computed: {
        hasItems() {
            return this.rows.length > 0;
        },
        uri() {
            let result = this.source;
            console.log(result);
            
            for(let [key, value] of Object.entries(this.uriBindings)) {
                if(value == null || value == "") {
                    return null;
                }

                result = result.replace(":" + key, value);
            }
            
            return result;
        }
    },

    watch: {
        uri: {
            handler(newUri) {
                if(newUri != null) {
                    this.getPage(1);
                }
            }
        },
        search: {
            handler(value) {
                if(value == "" && this.currentSearch != '') {
                    this.clearSearch();
                }
            }
        },
        error: {
            handler(value) {
                // If something goes wrong with the server, stop polling
                if(value && this.interval) {
                    clearInterval(this.interval);
                    this.interval = null;
                }
            }
        }
    },

    mounted() {
        // Gather all child column components in an object that can be referenced by the column's name
        for(let child of this.$children) {
            if(child.$options._componentTag == 'data-table-column') {
                this.columns.push(child);

                // Listen for click on each child
                child.$on('sort', event => {
                    this.sortBy(event.column, event.direction);
                })
            }
        }

        // Look through the columns to see if any have "initial" set to true
        // If so, replace the default column and direction
        for(let column of this.columns) {
            if(column.$props.initial == true) {
                this.sort.column = column.$props.column;
                this.sort.direction = column.$props.direction;

                break;
            }
        }

        // Add event listeners
        for(let listener of this.updateOn) {
            EventBus.$on(listener, event => {
                this.$nextTick(() => {
                    this.getPage(this.currentPage);
                });
            });
        }

        // Fetch page 1
        this.getPage(1);

        // Set up polling
        if(this.poll) {
            this.interval = setInterval(() => this.getPage(this.currentPage, false), this.poll)
        }
    },

    methods: {
        getPage(page, displayLoader = true) {
            
            if(page < 1) {
                return;
            }

            if(displayLoader) {
                this.loading = true;
            }

            // Build up the query string parameters
            let data = {};

            data.sort = this.sort.column;
            data.direction = this.sort.direction;

            // Start with passed in parameters
            data = Object.assign(data, this.additionalParams);

            // Include the page number, column to sort by, and sort direction
            if(this.paginate) {
                data.page = page;
            } else {
                data.page = 1;
                data.perPage = 0;
                data.paginate = false;
            }
            
            // Include a search, if any
            if(this.currentSearch != '') {
                data.search = this.currentSearch;
            }

            axios.get(this.uri, {
                params: data
            })
            .then(response => {
                this.currentPage = page;
                console.log(response);

                // Paginated data has a wrapper "data" key, while unpaginated data does not
                this.rows = this.paginate ? response.data.data : response.data;

                if(page != 1 && this.rows.length == 0) {
                    // The requested page has no results, reset to the previous page
                    this.getPage(page - 1);
                    return;
                }

                // Store additional data
                if(response.data.store) {
                    for(let [key, value] of Object.entries(response.data.store)) {
                        this.$set(this.store, key, value);
                    }
                }

                if(this.paginate) {
                    if(response.data.meta) {
                        this.links = response.data.meta;
                        this.results = response.data.meta.total;
                    }
                } else {
                    this.results = this.rows.length;
                }

                this.$nextTick(() => {
                    // Initialize Bootstrap tooltips
                    // It would be better to have a tooltip component that initialized itself on mount so I wouldn't have to worry about re-initializing it for instances like this
                    $('[data-toggle="tooltip"]').tooltip();
                });
            })
            .catch(error => {
                this.error = true;
                this.rows = [];
                this.results = 0;

                console.error(error);
            })
            .then(() => {
                this.loading = false;
            });
        },

        sortBy(column, direction) {
            this.sort.column = column;
            this.sort.direction = direction;

            // Update each child column's styles
            for(let x of this.columns) {
                x.update(x.$props.column == column, direction);
            }

            // Reset to page one
            this.getPage(1);
        },

        applySearch() {
            this.currentSearch = this.search;

            // Reset to page one
            this.getPage(1);
        },

        clearSearch() {
            this.search = '';

            this.applySearch();
        },

        addParameter(key, value) {
            this.$set(this.additionalParams, key, value);

            this.getPage(1);
        },

        removeRow(id) {
            for(let row of this.rows) {
                if(row.id == id) {
                    this.rows.splice(this.rows.indexOf(row), 1);
                    this.getPage(this.currentPage);
                    return;
                }
            }
        },

        refresh(useCurrentPage = false) {
            if(useCurrentPage){
                this.getPage(this.currentPage);
            }else{
                this.getPage(1);
            }
        }
    }
    
}
</script>

<style scoped>

th {
    padding-top: .8rem;
    padding-bottom: .8rem;
    padding-left: .5rem;
    padding-right: .5rem;
}

</style>
